import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Layout from '../components/layout/Layout'
import { useAuth } from '../hooks/useAuth'
import './AuthPage.css'

type AuthScreen = 'sign-in' | 'sign-up' | 'confirm-sign-up' | 'forgot-password' | 'confirm-reset-password'

function AuthPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    isAuthenticated,
    loading: authLoading,
    signInWithPassword,
    signUpWithPassword,
    confirmUserSignUp,
    resendConfirmationCode,
    requestPasswordReset,
    confirmPasswordReset,
    consumeReturnTo,
    setReturnTo
  } = useAuth()

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const initialMode = searchParams.get('mode') === 'sign-up' ? 'sign-up' : 'sign-in'

  const [screen, setScreen] = useState<AuthScreen>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    setScreen(initialMode)
  }, [initialMode])

  useEffect(() => {
    const returnTo = searchParams.get('returnTo')
    if (returnTo) {
      setReturnTo(returnTo)
    }
  }, [searchParams, setReturnTo])

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(consumeReturnTo('/dashboard'), { replace: true })
    }
  }, [authLoading, consumeReturnTo, isAuthenticated, navigate])

  const resetFeedback = () => {
    setError(null)
    setMessage(null)
  }

  const handleSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    resetFeedback()
    setLoading(true)

    try {
      const result = await signInWithPassword({ username: email, password })
      if (result.isSignedIn) {
        navigate(consumeReturnTo('/dashboard'), { replace: true })
        return
      }

      if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        setScreen('confirm-sign-up')
        setMessage(t('auth.confirmAccountHint'))
        return
      }

      setError(t('auth.unsupportedStep'))
    } catch (authError) {
      setError(getErrorMessage(authError, t('auth.signInFailed')))
    } finally {
      setLoading(false)
    }
  }

  const handleSignUp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    resetFeedback()

    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'))
      return
    }

    setLoading(true)
    try {
      const result = await signUpWithPassword({
        username: email,
        password,
        options: {
          userAttributes: {
            email
          }
        }
      })

      if (result.isSignUpComplete) {
        setMessage(t('auth.accountCreated'))
        setScreen('sign-in')
      } else {
        setScreen('confirm-sign-up')
        setMessage(t('auth.confirmationCodeSent'))
      }
    } catch (authError) {
      setError(getErrorMessage(authError, t('auth.signUpFailed')))
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmSignUp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    resetFeedback()
    setLoading(true)

    try {
      await confirmUserSignUp({ username: email, confirmationCode: code })
      setMessage(t('auth.accountConfirmed'))
      const result = await signInWithPassword({ username: email, password })
      if (result.isSignedIn) {
        navigate(consumeReturnTo('/dashboard'), { replace: true })
        return
      }
      setScreen('sign-in')
    } catch (authError) {
      setError(getErrorMessage(authError, t('auth.confirmationFailed')))
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    resetFeedback()
    setLoading(true)

    try {
      await requestPasswordReset({ username: email })
      setScreen('confirm-reset-password')
      setMessage(t('auth.resetCodeSent'))
    } catch (authError) {
      setError(getErrorMessage(authError, t('auth.resetRequestFailed')))
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmResetPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    resetFeedback()
    setLoading(true)

    try {
      await confirmPasswordReset({ username: email, confirmationCode: code, newPassword })
      setMessage(t('auth.passwordResetSuccess'))
      setScreen('sign-in')
      setPassword(newPassword)
      setNewPassword('')
      setCode('')
    } catch (authError) {
      setError(getErrorMessage(authError, t('auth.passwordResetFailed')))
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = async () => {
    resetFeedback()
    setLoading(true)

    try {
      await resendConfirmationCode({ username: email })
      setMessage(t('auth.confirmationCodeResent'))
    } catch (authError) {
      setError(getErrorMessage(authError, t('auth.confirmationResendFailed')))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Layout>
      <div className='auth-page'>
        <div className='auth-shell'>
          <div className='auth-intro'>
            <span className='auth-eyebrow'>{t('auth.eyebrow')}</span>
            <h1>{screen === 'sign-up' || screen === 'confirm-sign-up' ? t('auth.createTitle') : t('auth.signInTitle')}</h1>
            <p>{t('auth.subtitle')}</p>
            <div className='auth-intro-links'>
              <Link to='/registration'>{t('auth.marketplaceLink')}</Link>
              <Link to='/docs'>{t('nav.docs')}</Link>
            </div>
          </div>

          <div className='auth-card'>
            <div className='auth-tabs' role='tablist' aria-label={t('auth.tabLabel')}>
              <button
                type='button'
                className={`auth-tab ${screen === 'sign-in' || screen === 'forgot-password' || screen === 'confirm-reset-password' ? 'is-active' : ''}`}
                onClick={() => {
                  resetFeedback()
                  setScreen('sign-in')
                }}
              >
                {t('auth.signInTab')}
              </button>
              <button
                type='button'
                className={`auth-tab ${screen === 'sign-up' || screen === 'confirm-sign-up' ? 'is-active' : ''}`}
                onClick={() => {
                  resetFeedback()
                  setScreen('sign-up')
                }}
              >
                {t('auth.signUpTab')}
              </button>
            </div>

            {message ? <p className='auth-message auth-message--success'>{message}</p> : null}
            {error ? <p className='auth-message auth-message--error'>{error}</p> : null}

            {screen === 'sign-in' ? (
              <form className='auth-form' onSubmit={handleSignIn}>
                <label>
                  <span>{t('auth.emailLabel')}</span>
                  <input type='email' autoComplete='email' value={email} onChange={event => setEmail(event.target.value)} required />
                </label>
                <label>
                  <span>{t('auth.passwordLabel')}</span>
                  <input
                    type='password'
                    autoComplete='current-password'
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    required
                  />
                </label>
                <button type='submit' className='btn btn-primary auth-submit' disabled={loading}>
                  {loading ? t('auth.working') : t('auth.signInCta')}
                </button>
                <button type='button' className='btn btn-text auth-link-btn' onClick={() => setScreen('forgot-password')}>
                  {t('auth.forgotPassword')}
                </button>
              </form>
            ) : null}

            {screen === 'sign-up' ? (
              <form className='auth-form' onSubmit={handleSignUp}>
                <label>
                  <span>{t('auth.emailLabel')}</span>
                  <input type='email' autoComplete='email' value={email} onChange={event => setEmail(event.target.value)} required />
                </label>
                <label>
                  <span>{t('auth.passwordLabel')}</span>
                  <input
                    type='password'
                    autoComplete='new-password'
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    required
                  />
                </label>
                <label>
                  <span>{t('auth.confirmPasswordLabel')}</span>
                  <input
                    type='password'
                    autoComplete='new-password'
                    value={confirmPassword}
                    onChange={event => setConfirmPassword(event.target.value)}
                    required
                  />
                </label>
                <button type='submit' className='btn btn-primary auth-submit' disabled={loading}>
                  {loading ? t('auth.working') : t('auth.signUpCta')}
                </button>
              </form>
            ) : null}

            {screen === 'confirm-sign-up' ? (
              <form className='auth-form' onSubmit={handleConfirmSignUp}>
                <label>
                  <span>{t('auth.emailLabel')}</span>
                  <input type='email' autoComplete='email' value={email} onChange={event => setEmail(event.target.value)} required />
                </label>
                <label>
                  <span>{t('auth.codeLabel')}</span>
                  <input type='text' inputMode='numeric' value={code} onChange={event => setCode(event.target.value)} required />
                </label>
                <label>
                  <span>{t('auth.passwordLabel')}</span>
                  <input
                    type='password'
                    autoComplete='new-password'
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    required
                  />
                </label>
                <button type='submit' className='btn btn-primary auth-submit' disabled={loading}>
                  {loading ? t('auth.working') : t('auth.confirmAccountCta')}
                </button>
                <button type='button' className='btn btn-text auth-link-btn' onClick={() => void handleResendCode()}>
                  {t('auth.resendCode')}
                </button>
              </form>
            ) : null}

            {screen === 'forgot-password' ? (
              <form className='auth-form' onSubmit={handleForgotPassword}>
                <label>
                  <span>{t('auth.emailLabel')}</span>
                  <input type='email' autoComplete='email' value={email} onChange={event => setEmail(event.target.value)} required />
                </label>
                <button type='submit' className='btn btn-primary auth-submit' disabled={loading}>
                  {loading ? t('auth.working') : t('auth.sendResetCode')}
                </button>
              </form>
            ) : null}

            {screen === 'confirm-reset-password' ? (
              <form className='auth-form' onSubmit={handleConfirmResetPassword}>
                <label>
                  <span>{t('auth.emailLabel')}</span>
                  <input type='email' autoComplete='email' value={email} onChange={event => setEmail(event.target.value)} required />
                </label>
                <label>
                  <span>{t('auth.codeLabel')}</span>
                  <input type='text' inputMode='numeric' value={code} onChange={event => setCode(event.target.value)} required />
                </label>
                <label>
                  <span>{t('auth.newPasswordLabel')}</span>
                  <input
                    type='password'
                    autoComplete='new-password'
                    value={newPassword}
                    onChange={event => setNewPassword(event.target.value)}
                    required
                  />
                </label>
                <button type='submit' className='btn btn-primary auth-submit' disabled={loading}>
                  {loading ? t('auth.working') : t('auth.resetPasswordCta')}
                </button>
              </form>
            ) : null}
          </div>
        </div>
      </div>
    </Layout>
  )
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
    return error.message
  }

  return fallback
}

export default AuthPage